import { cssProps } from '@oracle/oraclejet-cssprops';
import { Ref } from 'preact';
import { useFormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
import { useTextFieldInputHandlers } from '../hooks/UNSAFE_useTextFieldInputHandlers';
import { classNames } from '../utils/UNSAFE_classNames';
import type { TextProps } from '../utils/UNSAFE_interpolations/text';
import { textInterpolations } from '../utils/UNSAFE_interpolations/text';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { ValueUpdateDetail } from '../utils/UNSAFE_valueUpdateDetail';
import { getLoadingAriaLabel } from './FormControlUtils';
import { isInputPlaceholderShown } from './TextFieldUtils';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';

const rootStyles = {
  base: cssProps`${{
    // normally removing an outline can cause accessibility issues, however
    // on an input the cursor blinks so an outline is not required for
    // accessibility. As of 6-11-14 only chrome is putting
    // an outline on inputs anyway
    outlineStyle: 'none',
    outlineWidth: '0',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    lineHeight: 'inherit',
    fontWeight: 'inherit',
    border: '0',
    backgroundColor: 'transparent',
    width: '100%',

    '&::selection': {
      backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-bg-color-selected)'
    }
  }}`,
  // removes margin that Safari adds to the input element
  input: cssProps`${{
    margin: '0'
  }}`,
  textarea: cssProps`${{
    paddingTop:
      'calc((var(--oj-c-PRIVATE-DO-NOT-USE-text-field-height) - 1rem * var(--oj-c-PRIVATE-DO-NOT-USE-typography-body-md-line-height))/ 2 - 1px)',
    paddingBottom:
      'calc((var(--oj-c-PRIVATE-DO-NOT-USE-text-field-height) - 1rem * var(--oj-c-PRIVATE-DO-NOT-USE-typography-body-md-line-height))/ 2 - 1px)',

    resize: 'none'
  }}`,
  password: cssProps`${{
    '&::-ms-reveal': {
      display: 'none'
    }
  }}`,
  labelDefaultPadding: cssProps`${{
    paddingTop: 0,
    paddingBottom: 0
  }}`,
  labelInsideBase: cssProps`${{
    minHeight: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-inside-edge-height)',
    paddingTop: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-inside-edge-padding-top)',
    paddingBottom: 0,

    '&:hover': {
      backgroundColor: 'transparent'
    }
  }}`,
  textareaLabelInsideBase: cssProps`${{
    paddingTop: 0,
    paddingBottom: 0,
    // We are using a transparent border here instead of padding, due to how textarea scrolls the padding off the screen
    // and the text content will clash with the inside label.
    borderColor: 'transparent',
    borderStyle: 'solid',
    borderBlockStartWidth:
      'calc(var(--oj-c-PRIVATE-DO-NOT-USE-label-inside-edge-font-size) * var(--oj-c-PRIVATE-DO-NOT-USE-label-inside-edge-line-height) + var(--oj-c-PRIVATE-DO-NOT-USE-label-inside-edge-position-top))',
    // The 0.25rem is an aesthetically pleasing amount of bottom padding
    borderBlockEndWidth: '0.25rem'
  }}`,

  labelInsideNoValueAndHasFocus: cssProps`${{
    animationName:
      'var(--oj-c-PRIVATE-DO-NOT-USE-private-preact-text-field-animation-name-placeholder)',
    animationDuration: 'var(--oj-c-PRIVATE-DO-NOT-USE-animation-duration-xlong)',
    animationTimingFunction: 'var(--oj-c-PRIVATE-DO-NOT-USE-animation-ease-in)'
  }}`,

  hasStartContent: cssProps`${{
    paddingInlineStart: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-icon-to-text-padding)'
  }}`,

  hasEndContent: cssProps`${{
    paddingInlineEnd: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-icon-to-text-padding)'
  }}`,

  defaultColor: cssProps`${{
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-text-color)'
  }}`,

  disabledColor: cssProps`${{
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-text-color-disabled)'
  }}`,
  // The 0.25em is the space between the prefix and the input. This is equivalent to ' '.
  hasPrefix: cssProps`${{
    paddingInlineStart: '.25em'
  }}`,

  hasSuffix: cssProps`${{
    paddingInlineEnd: '.25em'
  }}`,

  default: cssProps`${{
    fontSize: '100%'
  }}`,

  defaultPaddingStart: cssProps`${{
    paddingInlineStart: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-text-to-edge-padding)'
  }}`,

  defaultPaddingEnd: cssProps`${{
    paddingInlineEnd: 'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-text-to-edge-padding)'
  }}`,

  embedded: cssProps`${{
    color: 'inherit',
    fontSize: 'inherit',
    height: '100%',
    margin: 0,
    paddingBlock: 0,
    paddingInline:
      'var(--oj-c-PRIVATE-DO-NOT-USE-text-field-embedded-padding, var(--oj-c-PRIVATE-DO-NOT-USE-text-field-text-to-edge-padding))',

    // textarea styles
    resize: 'none',

    // input-password styles
    '&::-ms-reveal': {
      display: 'none'
    }
  }}`
};

const interpolations = [...Object.values(textInterpolations)];

const styleInterpolations = mergeInterpolations<Props>(interpolations);

type Autocomplete = 'off' | 'on' | string;

type OrigPickedPropsFromInput = Pick<
  HTMLAttributesSignalExcluded<HTMLInputElement>,
  'autofocus' | 'id' | 'placeholder' | 'required' | 'role' | 'spellcheck' | 'type'
>;
type RenamedPropsFromInput = Omit<OrigPickedPropsFromInput, 'autofocus' | 'required'> & {
  autoFocus?: OrigPickedPropsFromInput['autofocus'];
  isRequired?: OrigPickedPropsFromInput['required'];
};
type InputProps = {
  as?: 'input';
  type?: Pick<HTMLAttributesSignalExcluded<HTMLInputElement>, 'type'>['type'];
  rows?: never;
  inputRef?: Ref<HTMLInputElement>;
  hasPrefix?: boolean;
  hasSuffix?: boolean;
};

type TextAreaProps = {
  as: 'textarea';
  type?: never;
  rows?: number;
  inputRef?: Ref<HTMLTextAreaElement>;
  hasPrefix?: never;
  hasSuffix?: never;
};

type UniqueProps = InputProps | TextAreaProps;

type AriaProps = {
  ariaAutoComplete?: 'none' | 'inline' | 'list' | 'both';
  ariaControls?: string;
  ariaDescribedBy?: string;
  ariaExpanded?: boolean;
  ariaInvalid?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaValueMax?: number;
  ariaValueMin?: number;
  ariaValueNow?: number;
  ariaValueText?: string;
};

type Props = UniqueProps &
  TextProps &
  RenamedPropsFromInput &
  AriaProps & {
    autoComplete?: Autocomplete;
    // TODO: Should we expose has* props like these or should we pass this info
    // via FormFieldContext?  (from review on 3/8/22)
    currentCommitValue?: string;
    hasEndContent?: boolean;
    hasInsideLabel?: boolean;
    hasStartContent?: boolean;
    value?: string;
    variant?: 'default' | 'embedded';
    onInput?: (detail: ValueUpdateDetail<string>) => void;
    onCommit?: (detail: ValueUpdateDetail<string>) => void;
    onKeyDown?: (event: KeyboardEvent) => void;
    onKeyUp?: (event: KeyboardEvent) => void;
  };

export const TextFieldInput = ({
  as = 'input',
  ariaAutoComplete,
  ariaControls,
  ariaDescribedBy,
  ariaExpanded,
  ariaInvalid,
  ariaLabel,
  ariaLabelledBy,
  ariaValueMax,
  ariaValueMin,
  ariaValueNow,
  ariaValueText,
  autoComplete,
  autoFocus,
  currentCommitValue,
  hasEndContent = false,
  hasInsideLabel = false,
  hasPrefix = false,
  hasStartContent = false,
  hasSuffix = false,
  id,
  inputRef,
  placeholder,
  isRequired,
  role,
  rows,
  spellcheck,
  type,
  value = '',
  variant = 'default',
  onInput,
  onCommit,
  onKeyDown,
  onKeyUp,
  ...props
}: Props) => {
  const hasValue = value !== '';
  const { isDisabled, isFocused, isLoading, isReadonly } = useFormFieldContext();
  const myPlaceholder = isInputPlaceholderShown(hasInsideLabel, hasValue, isFocused)
    ? placeholder
    : undefined;
  const { class: styleInterpolationClasses } = styleInterpolations(props);
  const renderPrefix = !isDisabled && hasPrefix;
  const renderSuffix = !isDisabled && hasSuffix;
  const inputClasses = classNames([
    'oj-c-text-field-input',
    rootStyles.base,
    variant === 'default'
      ? classNames([
          rootStyles.default,
          as === 'textarea' && rootStyles.textarea,
          as === 'input' && rootStyles.input,
          type === 'password' && rootStyles.password,
          !hasStartContent && !renderPrefix && rootStyles.defaultPaddingStart,
          !hasEndContent && !renderSuffix && rootStyles.defaultPaddingEnd
        ])
      : rootStyles.embedded,
    hasInsideLabel &&
      classNames([
        rootStyles.labelInsideBase,
        as === 'textarea' && rootStyles.textareaLabelInsideBase,
        !hasValue && isFocused && rootStyles.labelInsideNoValueAndHasFocus
      ]),
    !hasInsideLabel && as !== 'textarea' && rootStyles.labelDefaultPadding,
    // We want the input to have padding on itself. This gives the user more space to click on to set focus to the input.
    // The padding between the input and the icon is different than the padding between the input and prefix/suffix (since they are both text)
    // startcontentpadding+input+endcontentpadding
    hasStartContent && !hasPrefix && rootStyles.hasStartContent,
    hasEndContent && !hasSuffix && rootStyles.hasEndContent,
    isDisabled ? rootStyles.disabledColor : rootStyles.defaultColor,
    // prefix+prefixpadding+input+suffixpadding+suffix
    // if there is also a startcontent or end content,
    // the prefix/suffix will have that padding on it (see PrefixSuffix)
    renderPrefix && rootStyles.hasPrefix,
    renderSuffix && rootStyles.hasSuffix,
    styleInterpolationClasses
  ]);
  const Comp = as || 'input';
  const handlers = useTextFieldInputHandlers({
    currentCommitValue,
    value,
    onInput,
    onCommit,
    onKeyDown
  });
  // the implicit default for tabindex on an input is 0, so do not explicitly set it.
  // JET-52914 - Implement Tabbable mode API contract in InputText
  // if not tabbable, spread tabbableModeProps on component
  const { isTabbable, tabbableModeProps } = useTabbableMode();
  // TODO: for autoComplete='off', need to configure attrs appropriately to make sure it
  // works across browsers and versions  (from review on 3/11/22)
  return (
    <Comp
      aria-autocomplete={ariaAutoComplete}
      aria-controls={ariaControls}
      aria-describedby={ariaDescribedBy}
      aria-expanded={ariaExpanded}
      aria-invalid={ariaInvalid}
      aria-label={isLoading ? getLoadingAriaLabel() : ariaLabel ? ariaLabel : undefined}
      aria-labelledby={ariaLabelledBy}
      aria-required={isRequired ? true : undefined}
      aria-valuemax={ariaValueMax}
      aria-valuemin={ariaValueMin}
      aria-valuenow={ariaValueNow}
      aria-valuetext={ariaValueText}
      autocomplete={autoComplete}
      autofocus={autoFocus}
      class={inputClasses}
      disabled={isDisabled}
      id={id}
      onKeyUp={onKeyUp}
      placeholder={myPlaceholder}
      readonly={isReadonly}
      // @ts-ignore
      ref={inputRef}
      role={role}
      rows={rows}
      spellcheck={spellcheck}
      type={type}
      value={value}
      {...handlers}
      {...(!isTabbable && tabbableModeProps)}
    />
  );
};
